import Serializable from "@/core/misc/mixins/Serializable";
import RpcResponseError from "@/core/services/Rpc/RpcResponseError";
import RpcMeta from "@/core/services/Rpc/RpcMeta";

/**
 * Класс для работы с ответами RPC сервера
 *
 * Структура ответа:
 * Успех: { id, data, meta, debug? }
 * Ошибка: { id, error, meta, debug? }
 */
class RpcResponse extends Serializable {

	/**
	 * Идентификатор запроса
	 */
	id: string | number;

	/**
	 * Данные ответа (null если есть error)
	 */
	data: any = null;

	/**
	 * Метаданные ответа (пагинация, сортировка и т.д.)
	 * Существует всегда: для несостоявшихся запросов — RpcMeta.createEmpty()
	 */
	meta: RpcMeta = null!;

	/**
	 * Debug-информация (breadcrumbs, queries)
	 * На верхнем уровне для удобства просмотра в DevTools
	 */
	debug: any = null;

	/**
	 * Ошибка выполнения RPC метода
	 */
	error: RpcResponseError | null = null;

	/**
	 * Уведомления от сервера
	 */
	notifications: string[] | null = null;

	/**
	 * Флаг отмены запроса
	 */
	cancelled: boolean = false;

	/**
	 * Создание экземпляра из данных сервера
	 */
	static fromServer(serverData: any): RpcResponse {
		const obj = new RpcResponse();

		obj.id = serverData.id;

		if (serverData.error) {
			obj.error = RpcResponseError.fromServer(serverData.error);
		} else {
			obj.data = serverData.data ?? null;
		}

		if (serverData.meta) {
			obj.meta = RpcMeta.fromServer(serverData.meta);
		} else {
			obj.meta = RpcMeta.createEmpty();
		}

		obj.debug = serverData.debug ?? null;

		return obj;
	}

	/**
	 * Создание экземпляра для транспортной ошибки (сеть, HTTP 500)
	 */
	static fromTransportError(error: Error, requestId: string): RpcResponse {

		const obj = new RpcResponse();

		obj.id = requestId;
		obj.data = null;
		obj.meta = RpcMeta.createEmpty();
		obj.error = RpcResponseError.fromTransportError(error);
		obj.notifications = null;
		obj.cancelled = false;

		return obj;
	}

	/**
	 * Создание экземпляра для таймаута запроса
	 */
	static fromTimeout(requestId: string): RpcResponse {

		const obj = new RpcResponse();

		obj.id = requestId;
		obj.data = null;
		obj.meta = RpcMeta.createEmpty();
		obj.error = RpcResponseError.fromTimeout();
		obj.notifications = null;
		obj.cancelled = false;

		return obj;
	}

	/**
	 * Создание экземпляра для отменённого запроса
	 */
	static fromCancellation(requestId: string): RpcResponse {

		const obj = new RpcResponse();

		obj.id = requestId;
		obj.data = null;
		obj.meta = RpcMeta.createEmpty();
		obj.error = null;
		obj.notifications = null;
		obj.cancelled = true;

		return obj;
	}

	/**
	 * Транспортный уровень
	 * Успешен ли запрос (нет ошибки и не отменён)
	 * private - потому что он не должен торчать наружу и смущать народ. Чтобы никто не ошибся случайно
	 */
	private isFulfilled(): boolean {
		return !this.error && !this.cancelled;
	}

	/**
	 * Успешен ли запрос
	 *
	 * Объединяет проверку транспортного уровня и бизнес-результата в одном геттере.
	 *
	 * Почему так:
	 * 1. Во всех сервисах паттерн один: `if (response.ok) { показать успех }`.
	 *    Заставлять разработчика каждый раз помнить про `&& response.data !== false` — источник багов.
	 * 2. Один путь — меньше ошибок — когда IDE предлагает один очевидный вариант,
	 *    разработчик не стоит перед выбором. Выбрал `ok` — и всё работает правильно.
	 * 3. Семантика для потребителя — сервису неважно почему операция не выполнилась
	 *    (сеть упала или бэкенд вернул false). Важно только: выполнилась или нет.
	 * 4. Вопрос о важности различать ошибку транспортного уровня
	 * 	  и data === false передо мной стоял примерно никогда. Крайние случаи мы всегда оставляем за скобками.
	 *
	 */
	get ok(): boolean {
		return this.isFulfilled()
			&& this.data !== false;
	}

	/**
	 * Синоним
	 */
	get isOk(): boolean {
		return this.ok;
	}

	/**
	 * Был ли запрос отменён
	 */
	get isCancelled(): boolean {
		return this.cancelled;
	}
}

export default RpcResponse;